import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RotaButton, RotaTab } from "@teamrota/rota-design";
import moment from "moment";

import { POOL_TYPES } from "@teamrota/rota-common";

import asyncConfirm from "~/src/utils/async-confirm";
import Select from "~/src/components/form-components/select";
import TextInput from "~/src/components/form-components/text-input";
import ShiftTagsViewModal from "./ShiftTagsViewModal";

import MemberAddWarningModal from "~/src/components/StaffZonesPanel/components/MemberAddWarningModal";
import { getMemberTypesDropdownValues } from "../../utils";
import RateBreakdown from "~/src/components/RateBreakdown";
import { ROLE_TABS } from "~/src/consts";

import FormStepSegment from "../FormStepSegment";
import { useValidateAudience } from "../shared";

import { useTagsForAccount } from "./use-tags-for-account";
import { useGetRelatedMember } from "./use-get-related-member";
import { TabsWrapper, TabsTitle, RoleAndCountWrapper } from "./styles";

import { Actions, Fieldset, Header, HeaderTitle, EditButton } from "../styles";

import {
  useDayRoleOptions,
  useCalculatedRoleOptions,
  useUncalculatedRoleOptions,
  useVenueOptions,
  useUpdateRoleRate,
  useUpdateVenue,
  useUpdateMemberType,
  getFormTitle,
  mapStateToProps
} from "./selectors";

const FormAudience = props => {
  const {
    shift,
    isProviderScheduler,
    selectedAccountId,
    isOpen,
    hasVisited,
    hasCompletedAllSteps,
    goToSelf,
    goToNext,
    handleShiftUpdate,
    stepNumber,
    isGridSelectedShift,
    filteredPartners,
    selectedPartnerId,
    setSelectedPartnerId,
    requesterProfileLoading
  } = props;

  const {
    tagsAccountName,
    tagsAvailableForShift,
    tagsCompulsoryForShift
  } = useTagsForAccount({
    roleRateId: shift.roleRateId,
    roleRateAccountId: shift?.privates?.roleRateAccount?.id,
    partnerAccountId: selectedPartnerId
  });

  const assignedMemberId = shift.assignedMemberIds?.[0];

  const [isDayRoles, setIsDayRoles] = useState(false);
  const [isRoleSelected, setIsRoleSelected] = useState(false);
  const [selectedTab, setSelectedTab] = useState(ROLE_TABS[0]);
  const [blacklistWarning, setBlacklistWarning] = useState(null);

  const partnersWithUncalculatedRates = filteredPartners?.filter(partner =>
    partner.roleRates.some(({ isUncalculatedRate }) => isUncalculatedRate)
  );

  const partnersWithCalculatedRates = filteredPartners?.filter(partner =>
    partner.roleRates.some(({ isUncalculatedRate }) => !isUncalculatedRate)
  );

  const partnersToDisplay =
    selectedTab === "Uncalculated"
      ? partnersWithUncalculatedRates
      : partnersWithCalculatedRates;

  const partnerDropdownValues = partnersToDisplay?.map(p => ({
    value: p.id,
    label: p.accountName
  }));

  const defaultSelectedPartnerId =
    filteredPartners?.length === 1 ? filteredPartners[0].id : selectedPartnerId;

  const handleTabSwitch = tab => {
    setSelectedTab(tab);
    setIsRoleSelected(false);
    handleShiftUpdate({
      roleRateId: ""
    });

    if (isProviderScheduler && filteredPartners?.length !== 1) {
      setSelectedPartnerId(null);
      handleShiftUpdate({
        venueId: ""
      });
    }
  };

  const { member, loading: relationshipLoading } = useGetRelatedMember({
    assignedMemberId,
    selectedAccountId
  });

  const checkRelationship = () => {
    if (member?.relationship?.poolType === POOL_TYPES.BLACKLISTED) {
      setBlacklistWarning({
        member,
        callback: () => goToNext()
      });
    } else {
      goToNext();
    }
  };

  const handleTagsModalOpen = async () => {
    const initialSelectedTags = shift.tags;

    const confirmResult = await asyncConfirm(
      "Select the tags to add to this shift",
      {
        confirmButtonText: "Save",
        falseButtonText: "Cancel",
        isConfirmButtonGreen: true,
        subComponent: () => (
          <ShiftTagsViewModal
            tagsAvailableForShift={tagsAvailableForShift}
            tagsCompulsoryForShift={tagsCompulsoryForShift}
            selectedShiftTags={shift.tags}
            handleShiftUpdate={handleShiftUpdate}
            tagsAccountName={tagsAccountName}
          />
        )
      }
    );

    if (!confirmResult) {
      handleShiftUpdate({ tags: initialSelectedTags });
    }
  };

  const dayRoleOptions = useDayRoleOptions(props);
  const calculatedRoleOptions = useCalculatedRoleOptions(props);
  const uncalculatedRoleOptions = useUncalculatedRoleOptions(props);
  const venueOptions = useVenueOptions(props);
  const onRoleRateChange = useUpdateRoleRate(props);
  const onMemberTypeChange = useUpdateMemberType(props);
  const onVenueChange = useUpdateVenue(props);
  const isAudienceValid = useValidateAudience(props);

  const getOptions = () => {
    let options;
    if (selectedTab === "Uncalculated") {
      options = uncalculatedRoleOptions;
    } else if (isDayRoles) {
      options = dayRoleOptions;
    } else {
      options = calculatedRoleOptions;
    }

    const shiftStartTime = moment(Object.values(shift.times)?.[0]?.startTime);

    const optionsArray = Object.values(options)[0];

    const filteredOptions = optionsArray?.filter(
      option =>
        shiftStartTime.isAfter(option.startDate) &&
        (shiftStartTime.isBefore(option.endDate) || !option.endDate)
    );

    return filteredOptions || [];
  };

  const options = getOptions();

  const saveCompulsoryTagsToShift = () => {
    if (tagsCompulsoryForShift) {
      const tags = tagsCompulsoryForShift.map(({ id, name }) => ({
        id,
        name,
        isCompulsory: true
      }));

      handleShiftUpdate({
        tags
      });
    }
  };

  useEffect(() => {
    if (tagsCompulsoryForShift?.length !== 0 && shift?.tags?.length === 0) {
      saveCompulsoryTagsToShift();
    }
  }, [tagsCompulsoryForShift]);

  useEffect(() => {
    if (isProviderScheduler && filteredPartners.length === 1) {
      setSelectedPartnerId(filteredPartners[0].id);
    }
  }, []);

  const shouldShowTagsButton =
    shift.roleRateId &&
    (tagsAvailableForShift?.length !== 0 ||
      tagsCompulsoryForShift?.length !== 0);

  return (
    <FormStepSegment
      isOpen={isOpen}
      stepNumber={stepNumber}
      title={() => (
        <Header>
          <HeaderTitle>
            {!!isAudienceValid && !isOpen
              ? getFormTitle(props)
              : "Staff Requirements"}
          </HeaderTitle>
          {!!hasVisited && !isOpen && (
            <EditButton onClick={goToSelf}>
              {isAudienceValid ? "Edit" : "Missing Info!"}
            </EditButton>
          )}
        </Header>
      )}
    >
      {() => (
        <div>
          <Fieldset>
            {selectedTab === "Calculated" && (
              <TabsWrapper>
                <TabsTitle>Choose a type of rate:</TabsTitle>
                <RotaTab
                  label="Hourly Rate"
                  onClick={() => {
                    setIsDayRoles(false);
                    setIsRoleSelected(false);
                  }}
                  size="sm"
                  isActive={!isDayRoles}
                />
                <RotaTab
                  label="Daily Rate"
                  onClick={() => {
                    setIsDayRoles(true);
                    setIsRoleSelected(false);
                  }}
                  size="sm"
                  isActive={isDayRoles}
                />
              </TabsWrapper>
            )}
            <TabsWrapper>
              <TabsTitle>Choose a type of role:</TabsTitle>
              {ROLE_TABS.map(tab => (
                <RotaTab
                  key={tab}
                  label={tab}
                  onClick={() => handleTabSwitch(tab)}
                  size="sm"
                  isActive={selectedTab === tab}
                />
              ))}
              <RotaButton
                variant="outlined"
                size="small"
                onClick={handleTagsModalOpen}
                disabled={!shouldShowTagsButton}
                style={{ marginLeft: "auto" }}
              >
                Edit Tags
              </RotaButton>
            </TabsWrapper>
            <RoleAndCountWrapper oneColumn={!isProviderScheduler}>
              {isProviderScheduler && (
                <Select
                  label="Select the partner"
                  isDisabled={filteredPartners.length === 1}
                  options={partnerDropdownValues}
                  shouldFixLabelHeight
                  placeholder="Partner"
                  value={defaultSelectedPartnerId}
                  onChange={value => {
                    setSelectedPartnerId(value);
                    handleShiftUpdate({
                      venueId: "",
                      roleRateId: ""
                    });
                  }}
                  isSmall
                />
              )}
              <Select
                isDisabled={!!isGridSelectedShift}
                label="Select the type of staff"
                options={getMemberTypesDropdownValues()}
                shouldFixLabelHeight
                placeholder="Staff Type"
                value={shift.memberType}
                onChange={onMemberTypeChange}
                isSmall
              />
            </RoleAndCountWrapper>
            <RoleAndCountWrapper>
              {!isGridSelectedShift && (
                <TextInput
                  label="How many do you need?"
                  isSmall
                  min={1}
                  isError={parseInt(shift.numberRequested || 0, 10) <= 0}
                  value={shift.numberRequested}
                  onChangeValue={numberRequested => {
                    handleShiftUpdate({
                      numberRequested
                    });
                  }}
                  shouldFixLabelHeight
                  type="number"
                />
              )}
              <Select
                label="Select the type of Role"
                options={options}
                shouldFixLabelHeight
                placeholder="Job Role"
                isLoading={requesterProfileLoading}
                isDisabled={isProviderScheduler && !selectedPartnerId}
                value={shift.roleRateId}
                onChange={roleRateId => {
                  setIsRoleSelected(true);
                  onRoleRateChange(roleRateId);
                }}
                isSmall
              />
            </RoleAndCountWrapper>

            <Select
              label="Select the Location"
              options={venueOptions}
              shouldFixLabelHeight
              placeholder="Location"
              isLoading={requesterProfileLoading}
              isDisabled={isProviderScheduler && !selectedPartnerId}
              value={shift.venueId}
              onChange={onVenueChange}
              isSmall
            />
            {isRoleSelected && <RateBreakdown roleRateId={shift?.roleRateId} />}
          </Fieldset>

          <Actions>
            <RotaButton
              {...(!!isAudienceValid && { onClick: checkRelationship })}
              disabled={!isAudienceValid || relationshipLoading}
            >
              {hasCompletedAllSteps ? "Done" : "Next"}
            </RotaButton>
          </Actions>

          <MemberAddWarningModal
            accountType={isProviderScheduler ? "provider" : "requester"}
            roleRateId={shift.roleRateId}
            warning={blacklistWarning}
            onClose={() => setBlacklistWarning(null)}
          />
        </div>
      )}
    </FormStepSegment>
  );
};

export default connect(mapStateToProps)(FormAudience);
